import { useState } from "react";
import { Plus } from "lucide-react";

import { leaveApi } from "../api";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveSetupTabs } from "../components/LeaveSetupTabs";
import { LeaveTypeForm } from "../components/LeaveTypeForm";
import { LeaveTypesTable } from "../components/LeaveTypesTable";
import { useAsyncData, useMutation } from "../hooks";
import type { LeaveType, LeaveTypeCreateRequest } from "../types";

export default function LeavePoliciesPage() {
  const confirm = useConfirm();
  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data, loading, error, reload } = useAsyncData(
    () => leaveApi.allLeaveTypes(),
    [],
  );

  const save = useMutation(async (payload: LeaveTypeCreateRequest) => {
    if (editing) {
      return leaveApi.updateLeaveType(editing.id, payload);
    }

    return leaveApi.createLeaveType(payload);
  });

  async function handleSubmit(payload: LeaveTypeCreateRequest) {
    await save.mutate(payload);
    setShowForm(false);
    setEditing(null);
    await reload();
  }

  async function handleToggleActive(item: LeaveType) {
    const action = item.active ? "deactivate" : "activate";

    if (!(await confirm(`Are you sure you want to ${action} ${item.name}?`))) {
      return;
    }

    if (item.active) {
      await leaveApi.deactivateLeaveType(item.id);
    } else {
      await leaveApi.activateLeaveType(item.id);
    }

    await reload();
  }

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Leave Types"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave/setup" label="Setup Hub" />
            <button
              type="button"
              onClick={() => { setEditing(null); setShowForm(true); }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              New Leave Type
            </button>
          </div>
        }
      />

      <LeaveSetupTabs />

      {error ? <LeaveError message={error} onRetry={reload} /> : null}
      {save.error ? <LeaveError message={save.error} /> : null}

      {showForm || editing ? (
        <LeaveTypeForm
          initialValue={editing}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      ) : null}

      {loading ? <LeaveLoading label="Loading leave types..." /> : null}

      {data ? (
        <LeaveTypesTable
          items={data}
          onEdit={(item) => {
            setEditing(item);
            setShowForm(true);
          }}
          onToggleActive={handleToggleActive}
        />
      ) : null}
    </div>
  );
}